import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { LeadForm } from "@/components/common/LeadForm";
import { AREA_DETAILS, SEO_SERVICES } from "@/data/seoServices";
import { AR_AREAS, AR_SERVICES } from "@/data/seoServicesAr";
import { MapPin, CheckCircle2, ArrowRight, Building2 } from "lucide-react";

export default function AreaLanding() {
  const { slug } = useParams();
  const { lang } = useLang();
  const baseArea = AREA_DETAILS[slug];
  if (!baseArea) return <Navigate to="/" replace />;
  const ar = lang === "ar" ? AR_AREAS[slug] || {} : {};
  const area = { ...baseArea, ...ar };

  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${COMPANY.brandFull} — ${area.name}`,
    description: area.metaDesc,
    telephone: COMPANY.phone,
    email: COMPANY.email,
    url: `https://${COMPANY.domain}/areas/${slug}`,
    areaServed: { "@type": "City", name: area.name },
    address: {
      "@type": "PostalAddress",
      addressLocality: area.name,
      addressCountry: "AE",
    },
  };

  // Pick 6 highlight services for this area
  const highlightedServices = [
    "deep-cleaning-uae",
    "hvac-maintenance-uae",
    "office-cleaning-uae",
    "annual-maintenance-contract",
    "villa-maintenance-uae",
    "plumbing-services-uae",
  ];

  return (
    <div data-testid={`area-landing-${slug}`}>
      <SEO title={area.metaTitle} description={area.metaDesc} jsonLd={localBusinessLd} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-30">
          <img src={area.image} alt={area.nameLong} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="container-orion relative py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {area.nameLong}
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              {lang === "ar"
                ? `إدارة المرافق والتنظيف في ${area.name}`
                : `Facility Management & Cleaning in ${area.name}`}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{area.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#quote"
                data-testid={`area-${slug}-cta-quote`}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              >
                Get a Free Quote
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(`Hi Orion — I need FM services in ${area.name}.`)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              >
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage list */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-eyebrow text-blue-700">Coverage</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              Neighbourhoods we serve in {area.name}.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Trained, uniformed teams dispatched from our nearest hub — typical mobilisation under
              60 minutes.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {area.coverage.map((c, i) => (
                <li key={i} className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-16 bg-slate-50">
        <div className="container-orion">
          <div className="text-eyebrow text-blue-700">Sectors we serve in {area.name}</div>
          <div className="mt-6 flex flex-wrap gap-3">
            {area.sectors.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700"
              >
                <Building2 className="w-3.5 h-3.5 text-blue-700" />
                {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Service links */}
      <section className="py-20 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow">Popular services</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              Trusted services across {area.name}.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {highlightedServices.map((s) => {
              const svc = SEO_SERVICES[s];
              if (!svc) return null;
              const svcAr = lang === "ar" ? AR_SERVICES[s] || {} : {};
              const svcMerged = { ...svc, ...svcAr };
              return (
                <Link
                  key={s}
                  to={`/services/${s}`}
                  data-testid={`area-${slug}-service-${s}`}
                  className="group bg-white border border-slate-200 rounded-2xl p-7 card-hover"
                >
                  <div className="text-eyebrow text-blue-700">{svcMerged.eyebrow}</div>
                  <h3 className="mt-3 font-heading text-lg font-medium text-slate-900 group-hover:text-blue-700 transition-colors">
                    {svcMerged.h1.replace(" in UAE", "").replace(" في الإمارات", "")} — {area.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{svcMerged.intro}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-700">
                    Learn more <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section id="quote" className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">Get a Free Quote</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              Tell us about your {area.name} facility.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              A senior consultant calls back within 1 business hour with a tailored proposal.
            </p>
            <div className="mt-8 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                Free site walkthrough across {area.name}
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-blue-700" />
                Local teams, local response times
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource={`area-${slug}`} simple />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
